/**
 * A render loop for canvases that are being captured by MediaRecorder.
 *
 * `captureStream()` only emits a frame when the canvas is painted, so whenever
 * this loop stops the recorded video freezes on its last frame while audio
 * keeps running. Two things routinely kill a naive
 * `requestAnimationFrame(draw)` recursion:
 *
 *   1. A throw inside the callback. The re-schedule normally sits at the bottom
 *      of the draw function, so one transient failure (a video element in a bad
 *      state, a detached canvas, a half-decoded image) ends the loop for good.
 *   2. Backgrounding the tab. Browsers throttle rAF to ~0 Hz when the page is
 *      hidden, so the capture stream stalls for as long as the user is away.
 *
 * This loop survives both: every tick is guarded, the re-schedule always
 * happens, and while the page is hidden it falls back to a timer so the capture
 * keeps receiving frames.
 */

export interface FrameLoopScheduler {
  requestFrame(callback: () => void): number;
  cancelFrame(handle: number): void;
  setTimer(callback: () => void, delayMs: number): number;
  clearTimer(handle: number): void;
  now(): number;
  isHidden(): boolean;
}

export interface FrameLoopOptions {
  /** Painter. Exceptions are caught and reported; they never stop the loop. */
  onFrame: (timestampMs: number) => void;
  onError?: (error: unknown, consecutiveErrors: number) => void;
  /** Frame rate used while the page is hidden and rAF is throttled. */
  hiddenFps?: number;
  scheduler?: Partial<FrameLoopScheduler>;
}

export interface FrameLoop {
  start(): void;
  stop(): void;
  readonly running: boolean;
  readonly frameCount: number;
  readonly errorCount: number;
}

export const DEFAULT_HIDDEN_FPS = 30;

export const createDefaultScheduler = (): FrameLoopScheduler => ({
  requestFrame: (callback) => window.requestAnimationFrame(() => callback()),
  cancelFrame: (handle) => window.cancelAnimationFrame(handle),
  setTimer: (callback, delayMs) => window.setTimeout(callback, delayMs),
  clearTimer: (handle) => window.clearTimeout(handle),
  now: () => performance.now(),
  isHidden: () =>
    typeof document !== "undefined" && document.visibilityState === "hidden",
});

export const createFrameLoop = (options: FrameLoopOptions): FrameLoop => {
  const scheduler: FrameLoopScheduler = {
    ...createDefaultScheduler(),
    ...options.scheduler,
  };
  const hiddenIntervalMs = Math.max(
    1,
    Math.round(1000 / (options.hiddenFps ?? DEFAULT_HIDDEN_FPS)),
  );

  let running = false;
  // Bumped on every start/stop so a callback queued by a previous run can never
  // resurrect the loop after it was stopped.
  let generation = 0;
  let frameHandle: number | null = null;
  let timerHandle: number | null = null;
  let frameCount = 0;
  let errorCount = 0;
  let consecutiveErrors = 0;

  const clearPending = () => {
    if (frameHandle !== null) {
      scheduler.cancelFrame(frameHandle);
      frameHandle = null;
    }
    if (timerHandle !== null) {
      scheduler.clearTimer(timerHandle);
      timerHandle = null;
    }
  };

  const schedule = (ownGeneration: number) => {
    if (scheduler.isHidden()) {
      timerHandle = scheduler.setTimer(() => {
        timerHandle = null;
        tick(ownGeneration);
      }, hiddenIntervalMs);
    } else {
      frameHandle = scheduler.requestFrame(() => {
        frameHandle = null;
        tick(ownGeneration);
      });
    }
  };

  const tick = (ownGeneration: number) => {
    if (!running || ownGeneration !== generation) return;

    frameCount += 1;
    try {
      options.onFrame(scheduler.now());
      consecutiveErrors = 0;
    } catch (error) {
      errorCount += 1;
      consecutiveErrors += 1;
      try {
        options.onError?.(error, consecutiveErrors);
      } catch {
        /* a broken error reporter must not stop the loop either */
      }
    }

    // Re-check: onFrame/onError may have stopped the loop.
    if (!running || ownGeneration !== generation) return;
    schedule(ownGeneration);
  };

  return {
    start() {
      if (running) return; // starting twice must not create two loops
      clearPending();
      generation += 1;
      running = true;
      consecutiveErrors = 0;
      tick(generation); // paint the first frame synchronously
    },
    stop() {
      running = false;
      generation += 1;
      clearPending();
    },
    get running() {
      return running;
    },
    get frameCount() {
      return frameCount;
    },
    get errorCount() {
      return errorCount;
    },
  };
};
